import superjson from 'superjson';

export interface ChatHistoryMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  conversationId?: string | null;
  attachment?: {
    name: string;
    type: string;
    content: string;
    size?: number;
  };
}

/** GET /_api/taskingbot/chat-history?limit=200&before=<id>&conversationId=<id> */
export async function fetchChatHistory(opts?: {
  limit?: number;
  before?: string;
  conversationId?: string;
}): Promise<{ messages: ChatHistoryMessage[] }> {
  const params = new URLSearchParams();
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.before) params.set('before', opts.before);
  if (opts?.conversationId) params.set('conversationId', opts.conversationId);
  const qs = params.toString();
  const res = await fetch(`/_api/taskingbot/chat-history${qs ? `?${qs}` : ''}`, {
    credentials: 'include',
  });
  const text = await res.text();
  if (!res.ok) {
    try {
      const err = superjson.parse<{ error: string }>(text);
      throw new Error(err.error);
    } catch (e) {
      if (e instanceof Error && !e.message.includes('parse')) throw e;
    }
    throw new Error(text || `Failed to load chat history (${res.status})`);
  }
  try {
    return superjson.parse(text);
  } catch {
    return JSON.parse(text);
  }
}

/** POST /_api/taskingbot/chat-history — save messages */
export async function saveChatHistory(
  messages: Array<{
    role: 'user' | 'assistant';
    content: string;
    attachment?: { name: string; type: string; content: string; size?: number } | null;
  }>,
  conversationId?: string,
): Promise<{ saved: number; ids: number[] }> {
  const res = await fetch('/_api/taskingbot/chat-history', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: superjson.stringify({ messages, conversationId: conversationId || null }),
  });
  const text = await res.text();
  if (!res.ok) {
    try {
      const err = superjson.parse<{ error: string }>(text);
      throw new Error(err.error);
    } catch (e) {
      if (e instanceof Error && !e.message.includes('parse')) throw e;
    }
    throw new Error(text || `Failed to save chat history (${res.status})`);
  }
  try {
    return superjson.parse(text);
  } catch {
    return JSON.parse(text);
  }
}

/** DELETE /_api/taskingbot/chat-history — clear all or by conversationId */
export async function clearChatHistory(conversationId?: string): Promise<{ cleared: boolean }> {
  const params = new URLSearchParams();
  if (conversationId) params.set('conversationId', conversationId);
  const qs = params.toString();
  const res = await fetch(`/_api/taskingbot/chat-history${qs ? `?${qs}` : ''}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const text = await res.text();
  if (!res.ok) {
    try {
      const err = superjson.parse<{ error: string }>(text);
      throw new Error(err.error);
    } catch (e) {
      if (e instanceof Error && !e.message.includes('parse')) throw e;
    }
    throw new Error(text || `Failed to clear chat history (${res.status})`);
  }
  try {
    return superjson.parse(text);
  } catch {
    return JSON.parse(text);
  }
}

// ── Conversation management client functions ───────────────────────────

export interface TaskingBotConversation {
  id: string;
  userId: number;
  title: string;
  projectId?: string | null;
  isPinned: boolean;
  messageCount: number;
  lastMessage?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** GET /_api/taskingbot/conversations */
export async function fetchConversations(opts?: {
  limit?: number;
  projectId?: string;
}): Promise<{ conversations: TaskingBotConversation[] }> {
  const params = new URLSearchParams();
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.projectId) params.set('projectId', opts.projectId);
  const qs = params.toString();
  const res = await fetch(`/_api/taskingbot/conversations${qs ? `?${qs}` : ''}`, {
    credentials: 'include',
  });
  const text = await res.text();
  if (!res.ok) throw new Error(text || `Failed to load conversations (${res.status})`);
  try { return superjson.parse(text); } catch { return JSON.parse(text); }
}

/** POST /_api/taskingbot/conversations — create */
export async function createConversation(opts?: {
  title?: string;
  projectId?: string;
}): Promise<{ conversation: TaskingBotConversation }> {
  const res = await fetch('/_api/taskingbot/conversations', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: superjson.stringify({ title: opts?.title, projectId: opts?.projectId }),
  });
  const text = await res.text();
  if (!res.ok) throw new Error(text || `Failed to create conversation (${res.status})`);
  try { return superjson.parse(text); } catch { return JSON.parse(text); }
}

/** POST /_api/taskingbot/conversations/update */
export async function updateConversation(opts: {
  conversationId: string;
  title?: string;
  projectId?: string | null;
  isPinned?: boolean;
}): Promise<{ success: boolean }> {
  const res = await fetch('/_api/taskingbot/conversations/update', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: superjson.stringify(opts),
  });
  const text = await res.text();
  if (!res.ok) throw new Error(text || `Failed to update conversation (${res.status})`);
  try { return superjson.parse(text); } catch { return JSON.parse(text); }
}

/** POST /_api/taskingbot/conversations/delete */
export async function deleteConversation(conversationId: string): Promise<{ success: boolean }> {
  const res = await fetch('/_api/taskingbot/conversations/delete', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: superjson.stringify({ conversationId }),
  });
  const text = await res.text();
  if (!res.ok) throw new Error(text || `Failed to delete conversation (${res.status})`);
  try { return superjson.parse(text); } catch { return JSON.parse(text); }
}
